import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../api";
import { readableOn } from "../lib/colors";
import CheckoutModal from "../components/CheckoutModal";
import { applyMeta } from "../lib/meta";
import { CategoryTag } from "../lib/categories";
import { Loader2, ArrowLeft, CalendarDays, MapPin, Ticket, ZoomIn, X, ChevronLeft, ChevronRight, Lock, Utensils } from "lucide-react";
import { AddToCalendar } from "../components/EventExtras";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(iso).slice(0, 16).replace("T", " "); }
};

const fmtEnd = (startIso, endIso) => {
  if (!endIso) return "";
  try {
    const s = new Date(startIso), e = new Date(endIso);
    const sameDay = s.toDateString() === e.toDateString();
    return e.toLocaleString("en-GB", sameDay
      ? { hour: "2-digit", minute: "2-digit" }
      : { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
  } catch { return String(endIso).slice(11, 16); }
};

export default function ProductPage() {
  const { slug, productSlug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [buy, setBuy] = useState(false);
  const [waitTier, setWaitTier] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(null); // index of image shown full-size, or null
  const [pw, setPw] = useState("");
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i === null ? i : (i + 1) % Math.max(1, imagesCount)));
      if (e.key === "ArrowLeft") setLightbox((i) => (i === null ? i : (i - 1 + imagesCount) % Math.max(1, imagesCount)));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox]);

  useEffect(() => {
    api.get(`/store/${slug}/events/${productSlug}`)
      .then(({ data }) => {
        setData(data);
        if (data.locked) return;
        const p = data.product, b = data.business;
        applyMeta({ title: p.seo_title || `${p.title} — ${b.name}`, description: p.seo_description || p.description || `Get tickets for ${p.title} at ${b.name}.`, image: p.share_image || p.image_url || b.hero_image || b.logo_url });
      })
      .catch(() => setError("Event not found"));
  }, [slug, productSlug]);

  const unlock = async (e) => {
    e.preventDefault();
    setPwBusy(true); setPwError("");
    try {
      const { data } = await api.get(`/store/${slug}/events/${productSlug}?pw=${encodeURIComponent(pwInput)}`);
      if (data.locked) { setPwError("Incorrect password. Please try again."); }
      else { setPw(pwInput); setData(data); }
    } catch { setPwError("Something went wrong. Please try again."); }
    finally { setPwBusy(false); }
  };

  if (error) return <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-[#5a6b80]">{error}<Link to={`/store/${slug}`} className="gp-btn-teal rounded-full px-6 py-2.5 font-medium">Back to events</Link></div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-[#2ec9c9]" size={40} /></div>;

  if (data.locked) {
    const b = data.business, lp = data.product, lbrand = b.brand_color || "#16243b";
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: lbrand }} data-testid="event-locked">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-[420px] p-8 text-center">
          {b.logo_url ? <img src={b.logo_url} alt={b.name} className="h-12 mx-auto mb-4 object-contain" /> : null}
          <Lock size={30} className="mx-auto mb-3" style={{ color: lbrand }} />
          <h1 className="text-xl font-bold text-[#16243b]">{lp.title}</h1>
          <p className="text-sm text-[#5a6b80] mt-1 mb-5">This is a private event. Enter the password to view tickets.</p>
          <form onSubmit={unlock} className="space-y-3">
            <input type="password" value={pwInput} onChange={(e) => setPwInput(e.target.value)} placeholder="Event password" autoFocus data-testid="event-password-input"
              className="w-full rounded-xl border border-gray-200 px-4 py-3 outline-none focus:border-[#2ec9c9] focus:ring-2 focus:ring-[#2ec9c9]/20 transition text-center" />
            {pwError && <p className="text-sm text-red-500" data-testid="event-password-error">{pwError}</p>}
            <button type="submit" disabled={pwBusy || !pwInput} data-testid="event-password-submit" className="w-full rounded-xl py-3 font-medium text-white disabled:opacity-60" style={{ background: lbrand }}>
              {pwBusy ? "Checking…" : "Unlock event"}
            </button>
          </form>
          <Link to={`/store/${b.slug}`} className="inline-block mt-4 text-xs text-[#8a97a8] hover:underline">← Back to {b.name}</Link>
        </div>
      </div>
    );
  }

  const b = data.business;
  const p = data.product;
  const brand = b.brand_color || "#16243b";
  const btnColor = b.button_color || "";
  const btnText = b.ticket_button_text_color || (btnColor ? readableOn(btnColor) : "");
  const accentColor = b.ticket_accent_color || "";
  const images = [p.image_url, ...((p.gallery_images) || [])].filter(Boolean);
  const imagesCount = images.length;
  const main = images[activeImg] || images[0];
  const tiers = p.ticket_tiers || [];
  const prices = tiers.length ? tiers.map((t) => t.price) : [p.price];
  const from = Math.min(...prices);
  const multiPrice = new Set(prices).size > 1;
  const soldPct = p.show_sold_bar && typeof p.sold_pct === "number" ? p.sold_pct : null;

  return (
    <div className="min-h-screen bg-[#f6f8fb]">
      <div className="py-5" style={{ background: brand }}>
        <div className="max-w-[1000px] mx-auto px-6 flex items-center justify-between">
          <Link to={`/store/${b.slug}`} className="text-white/90 hover:text-white flex items-center gap-1.5 text-sm font-medium"><ArrowLeft size={16} /> {b.name}</Link>
          {b.logo_url && <img src={b.logo_url} alt={b.name} className="h-8 object-contain" />}
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-6 py-12 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="relative rounded-2xl overflow-hidden bg-white shadow-[0_15px_40px_rgba(20,30,55,0.1)] aspect-[4/3] group">
            {main ? (
              <button type="button" onClick={() => setLightbox(activeImg)} data-testid="event-image-zoom"
                className="w-full h-full block cursor-zoom-in" aria-label="View full-size image">
                <img src={main} alt={p.title} className="w-full h-full object-cover" />
                <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 bg-black/55 backdrop-blur-sm text-white text-xs font-medium px-2.5 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <ZoomIn size={14} /> View full image
                </span>
              </button>
            ) : (
              <div className="w-full h-full flex items-center justify-center" style={{ background: brand }}><Ticket className="text-white/70" size={48} /></div>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4 flex-wrap">
              {images.map((src, i) => (
                <button key={i} onClick={() => { setActiveImg(i); setLightbox(i); }} data-testid={`event-thumb-${i}`}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-colors cursor-zoom-in ${i === activeImg ? "border-[#2ec9c9]" : "border-transparent hover:border-gray-300"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <h1 className="text-[2.2rem] font-bold text-[#16243b] leading-tight" style={(b.ticket_text_color || b.text_color) ? { color: b.ticket_text_color || b.text_color } : undefined}>{p.title}</h1>
          {p.cancelled && (
            <div className="mt-4 rounded-2xl border border-red-200 bg-[#fdecec] p-5" data-testid="event-cancelled-notice">
              <p className="font-bold text-[#c0392b] text-lg">This event has been cancelled</p>
              {p.cancellation_message && (
                <p className="text-[#16243b] mt-2 text-sm bg-white/60 rounded-lg px-3 py-2 border border-red-100" data-testid="event-cancelled-message">{p.cancellation_message}</p>
              )}
              <p className="text-[#a5352c] mt-1.5 text-sm leading-relaxed">
                Refunds are on their way to all ticket holders. For more information, email{" "}
                <a href="mailto:hello@ticketspro.app" className="font-semibold underline" data-testid="event-cancelled-email">hello@ticketspro.app</a>.
              </p>
            </div>
          )}
          {p.postponed && !p.cancelled && (
            <div className="mt-4 rounded-2xl border border-amber-200 bg-[#fff7ed] p-5" data-testid="event-postponed-notice">
              <p className="font-bold text-[#b45309] text-lg">This event has been postponed</p>
              {p.postpone_message && (
                <p className="text-[#16243b] mt-2 text-sm bg-white/60 rounded-lg px-3 py-2 border border-amber-100" data-testid="event-postponed-message">{p.postpone_message}</p>
              )}
              <p className="text-[#92400e] mt-1.5 text-sm leading-relaxed">Your existing tickets remain valid. Questions? Email <a href="mailto:hello@ticketspro.app" className="font-semibold underline">hello@ticketspro.app</a>.</p>
            </div>
          )}
          {p.ended && !p.cancelled && (
            <div className="mt-4 rounded-2xl border border-gray-200 bg-[#f6f8fb] p-5" data-testid="event-ended-notice">
              <p className="font-bold text-[#16243b] text-lg">This event has ended</p>
              <p className="text-[#5a6b80] mt-1.5 text-sm leading-relaxed">Tickets are no longer available for this event. Browse the organiser's upcoming events instead.</p>
              <Link to={`/store/${slug}`} data-testid="ended-see-upcoming-btn" className="inline-flex items-center gap-2 mt-3 rounded-full px-6 py-2.5 text-sm font-medium text-white transition-colors" style={{ background: brand }}>
                <Ticket size={16} /> See upcoming events
              </Link>
            </div>
          )}
          {p.category && <div className="mt-2"><CategoryTag category={p.category} size={13} /></div>}
          <div className="mt-4 space-y-2">
            {p.event_date && <p className="text-[#16243b] flex items-center gap-2 font-medium"><CalendarDays size={18} className="text-[#2ec9c9]" /> {fmtDate(p.event_date)}{p.event_end ? ` – ${fmtEnd(p.event_date, p.event_end)}` : ""}</p>}
            {p.venue_name && <p className="text-[#5a6b80] flex items-center gap-2"><MapPin size={18} className="text-[#2ec9c9]" style={accentColor ? { color: accentColor } : undefined} /> {p.venue_slug && p.city_slug ? <Link to={`/venues/${p.city_slug}/${p.venue_slug}`} data-testid="product-venue-link" className="hover:text-[#2ec9c9] hover:underline">{p.venue_name}</Link> : p.venue_name}{p.venue_address ? `, ${p.venue_address}` : ""}</p>}
          </div>
          {(p.acts || []).length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="product-acts">
              <span className="text-xs uppercase tracking-wide text-[#8a97a8]">Featuring</span>
              {p.acts.map((a) => (
                <Link key={a.id} to={`/store/${slug}/acts/${a.slug}`} data-testid={`product-act-${a.id}`}
                  className="text-sm font-medium rounded-full px-3 py-1 bg-[#8b5cf6]/10 text-[#7c3aed] hover:bg-[#8b5cf6]/20 transition-colors">
                  {a.name}
                </Link>
              ))}
            </div>
          )}
          {(p.lineup || []).length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="product-lineup">
              <span className="text-xs uppercase tracking-wide text-[#8a97a8]">Lineup</span>
              {p.lineup.map((a) => (
                <Link key={a.id} to={`/artists/${a.slug}`} data-testid={`product-artist-${a.id}`}
                  className="text-sm font-medium rounded-full px-3 py-1 bg-[#2ec9c9]/10 text-[#1fb0b0] hover:bg-[#2ec9c9]/20 transition-colors">
                  {a.name}{a.set_time ? <span className="opacity-70 ml-1">· {a.set_time}</span> : null}
                </Link>
              ))}
            </div>
          )}
          {!p.tickets_disabled && !p.ended && <p className="text-[1.6rem] font-bold mt-4" style={{ color: accentColor || brand }} data-testid="product-from-price">{from <= 0 ? "Free entry" : `${multiPrice ? "from " : ""}£${from.toFixed(2)}`}</p>}
          {soldPct !== null && !p.tickets_disabled && !p.ended && !p.cancelled && (
            <div className="mt-4" data-testid="event-sold-bar">
              <div className="flex items-center justify-between text-sm font-semibold text-[#5a6b80] mb-1.5">
                <span>{soldPct >= 100 ? "Sold out" : `${soldPct}% sold`}</span>
                {soldPct >= 100 && <span className="text-[#c0392b]">Sold out</span>}
              </div>
              <div className="h-3 rounded-full bg-gray-200 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(3, Math.min(100, soldPct))}%`, background: soldPct >= 90 ? "#c0392b" : brand }} />
              </div>
            </div>
          )}
          {p.description && <p className="text-[#5a6b80] text-[1.05rem] mt-4">{p.description}</p>}
          {p.long_description && <p className="text-[#5a6b80] leading-relaxed mt-4 whitespace-pre-line">{p.long_description}</p>}

          {/* Tiers preview */}
          {p.ended ? null : p.tickets_disabled ? (
            <div className="mt-7 rounded-2xl border border-[#2ec9c9]/40 bg-[#eefafa] px-6 py-5" data-testid="no-tickets-notice">
              <p className="font-semibold text-[#16243b] flex items-center gap-2"><Ticket size={18} style={{ color: brand }} /> No tickets needed</p>
              <p className="text-sm text-[#5a6b80] mt-1">This is a free event — just turn up on the day. Add it to your calendar below so you don't forget.</p>
            </div>
          ) : (
          <div className="mt-6 space-y-2">
            {tiers.map((t) => {
              const isFree = t.free || t.price <= 0;
              const soldOut = p.sold_out || (t.capacity > 0 && (t.sold || 0) >= t.capacity);
              return (
                <div key={t.id} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <div>
                    <p className="font-semibold text-[#16243b]">{t.name}</p>
                    {t.capacity > 0 && (
                      soldOut ? (
                        <p className="text-xs text-[#8a97a8]">Sold out</p>
                      ) : (t.capacity - (t.sold || 0)) <= 10 ? (
                        <p className="text-xs font-semibold text-[#c0392b]" data-testid={`low-stock-${t.id}`}>Only {t.capacity - (t.sold || 0)} left!</p>
                      ) : (
                        <p className="text-xs text-[#8a97a8]">{t.capacity - (t.sold || 0)} left</p>
                      )
                    )}
                  </div>
                  {soldOut ? (
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-[#8a97a8] line-through">{isFree ? "Free" : `£${t.price.toFixed(2)}`}</span>
                      {!p.cancelled && (
                        <button onClick={() => setWaitTier(t)} data-testid={`join-waitlist-${t.id}`}
                          className="text-sm font-medium text-[#1fb0b0] border border-[#2ec9c9] rounded-full px-4 py-1.5 hover:bg-[#eefafa] transition-colors">Join waitlist</button>
                      )}
                    </div>
                  ) : (
                    <span className={`font-bold ${isFree ? "text-green-600" : "text-[#2ec9c9]"}`} style={!isFree && accentColor ? { color: accentColor } : undefined} data-testid={`tier-price-${t.id}`}>{isFree ? "Free" : `£${t.price.toFixed(2)}`}</span>
                  )}
                </div>
              );
            })}
          </div>
          )}

          {p.ended ? null : p.tickets_disabled ? null : p.cancelled ? null : p.sold_out ? (
            <div className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#fdecec] text-[#c0392b] font-semibold px-8 py-3.5 text-[1.05rem]" data-testid="sold-out-badge">
              <Ticket size={18} /> Sold out
            </div>
          ) : (
            <button onClick={() => setBuy(true)} data-testid="book-tickets-btn" className={btnColor ? "rounded-full px-8 py-3.5 text-[1.05rem] font-medium mt-7 transition-opacity hover:opacity-90" : "gp-btn-teal rounded-full px-8 py-3.5 text-[1.05rem] font-medium mt-7"} style={btnColor || btnText ? { ...(btnColor ? { background: btnColor } : {}), ...(btnText ? { color: btnText } : {}) } : undefined}>{from <= 0 ? "Get free ticket" : "Get tickets"}</button>
          )}
          {p.event_date && (
            <div className="mt-4">
              <AddToCalendar event={{ title: p.title, iso: p.event_date, location: [p.venue_name, p.venue_address].filter(Boolean).join(", "), details: p.description || "" }} />
            </div>
          )}

          {p.is_dinner && p.food_menu_url && (
            <div className="mt-8 border-t border-gray-200 pt-6" data-testid="food-menu-section">
              <h3 className="font-semibold text-[#16243b] mb-1 flex items-center gap-2"><Utensils size={18} style={{ color: brand }} /> Dinner menu</h3>
              <p className="text-sm text-[#5a6b80] mb-3">This event includes food. View or download the menu below.</p>
              <a href={p.food_menu_url} target="_blank" rel="noopener noreferrer" data-testid="view-food-menu-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[#2ec9c9] text-[#1fb0b0] font-medium px-6 py-2.5 hover:bg-[#eefafa] transition-colors">
                <Utensils size={16} /> View food menu (PDF)
              </a>
            </div>
          )}

          {p.terms && (
            <div className="mt-8 border-t border-gray-200 pt-6">
              <h3 className="font-semibold text-[#16243b] mb-2">Terms &amp; conditions</h3>
              <p className="text-sm text-[#5a6b80] leading-relaxed whitespace-pre-line">{p.terms}</p>
            </div>
          )}

          {(data.series_dates || []).length > 1 && (
            <div className="mt-8 border-t border-gray-200 pt-6" data-testid="series-dates">
              <h3 className="font-semibold text-[#16243b] mb-3 flex items-center gap-2"><CalendarDays size={18} className="text-[#2ec9c9]" /> More dates for this event</h3>
              <div className="flex flex-wrap gap-2">
                {data.series_dates.map((s) => (
                  s.current ? (
                    <span key={s.slug} data-testid="series-current" className="px-3 py-2 rounded-xl text-sm font-medium bg-[#2ec9c9] text-white">{fmtDate(s.event_date)}</span>
                  ) : (
                    <Link key={s.slug} to={`/store/${b.slug}/events/${s.slug}`} data-testid={`series-date-${s.slug}`}
                      className="px-3 py-2 rounded-xl text-sm font-medium bg-[#eefafa] text-[#16243b] hover:bg-[#2ec9c9] hover:text-white transition-colors">{fmtDate(s.event_date)}</Link>
                  )
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <footer className="text-center py-8 text-[#8a97a8] text-sm">Powered by TicketsPro.app</footer>

      {buy && <CheckoutModal slug={b.slug} product={p} checkoutFees={data.checkout_fees} accessPassword={pw} buttonColor={btnColor} buttonTextColor={btnText} onClose={() => setBuy(false)} />}
      {waitTier && <WaitlistModal slug={b.slug} product={p} tier={waitTier} onClose={() => setWaitTier(null)} />}

      {lightbox !== null && images[lightbox] && (
        <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4 sm:p-8"
          onClick={() => setLightbox(null)} data-testid="event-lightbox">
          <button onClick={() => setLightbox(null)} data-testid="lightbox-close"
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" aria-label="Close">
            <X size={22} />
          </button>
          {imagesCount > 1 && (
            <>
              <button data-testid="lightbox-prev" aria-label="Previous image"
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + imagesCount) % imagesCount); }}
                className="absolute left-3 sm:left-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
                <ChevronLeft size={24} />
              </button>
              <button data-testid="lightbox-next" aria-label="Next image"
                onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % imagesCount); }}
                className="absolute right-3 sm:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors">
                <ChevronRight size={24} />
              </button>
            </>
          )}
          <img src={images[lightbox]} alt={p.title} onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-[88vh] object-contain rounded-lg shadow-2xl" data-testid="lightbox-image" />
          {imagesCount > 1 && (
            <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-sm bg-black/40 px-3 py-1 rounded-full">
              {lightbox + 1} / {imagesCount}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function WaitlistModal({ slug, product, tier, onClose }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/store/${slug}/waitlist`, { product_id: product.id, tier_id: tier.id, ...form, origin: window.location.origin });
      setDone(true);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-[420px] p-6" onClick={(e) => e.stopPropagation()} data-testid="waitlist-modal">
        {done ? (
          <div className="text-center py-4">
            <h3 className="font-bold text-[#16243b] text-lg">You're on the list! 🎉</h3>
            <p className="text-[#5a6b80] text-sm mt-2">We'll email you if <strong>{tier.name}</strong> tickets for {product.title} become available.</p>
            <button onClick={onClose} className="gp-btn-teal rounded-xl px-6 py-2.5 font-medium mt-5" data-testid="waitlist-done-btn">Done</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <h3 className="font-bold text-[#16243b] text-lg">Join the waitlist</h3>
            <p className="text-[#5a6b80] text-sm mt-1 mb-4"><strong>{tier.name}</strong> is sold out. Leave your details and we'll let you know if tickets free up.</p>
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="waitlist-name"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] mb-3" />
            <input required type="email" placeholder="Your email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="waitlist-email"
              className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] mb-4" />
            <button disabled={loading} data-testid="waitlist-submit" className="gp-btn-teal w-full rounded-xl py-3 font-medium disabled:opacity-60">{loading ? "Joining…" : "Notify me"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
